import React, { Component } from "react"
import PropTypes from "prop-types"
import isEqual from "lodash.isequal"

import { QUORUM_BLUE, MIDDLE_PURPLE } from "shared/imports/sharedConstants"
import { formatNumberForDisplay } from "shared/imports/sharedHelperFunctions"

export default class BarGraph extends Component {
    static propTypes = {
        isLoading: PropTypes.bool.isRequired,
        updateGraph: PropTypes.func,
        data: PropTypes.object,
        selectedValues: PropTypes.array.isRequired,
        colorConfigs: PropTypes.object,
    }

    static defaultProps = {
        colorConfigs: {
            primaryColor: QUORUM_BLUE,
            secondaryColor: MIDDLE_PURPLE,
        },
    }

    componentDidMount() {
        this.setupGraph()
    }

    componentDidUpdate(oldProps) {
        const loadingFinished = oldProps.isLoading && !this.props.isLoading
        const newData = !this.props.data.slice(0, 10).equals(oldProps.data.slice(0, 10))
        const colorChange = !isEqual(oldProps.colorConfigs, this.props.colorConfigs)

        if (loadingFinished || newData) {
            this.clearGraph()
            this.applyData()
        } else if (oldProps.selectedValues.length !== this.props.selectedValues.length || colorChange) {
            this.updateBars()
        }
    }

    componentWillUnmount() {
        d3.select(".d3-tip").remove()
    }

    setupGraph() {
        this.svgWidth = parseInt(d3.select(this.node).style("width"))
        this.svgHeight = parseInt(d3.select(this.node).style("height"))

        d3.select(this.node).attr("width", `${this.svgWidth}px`).attr("height", `${this.svgHeight}px`)

        if (this.props.data.size) {
            this.applyData()
        }
    }

    getXAxisLabelWidth() {
        return 60
    }

    getYAxisLabelWidth() {
        const labels = d3.select(this.node).selectAll("test-y-axis-labels").data(this.data)

        let maxWidth = 0

        labels
            .enter()
            .append("text")
            .attr("class", "tick test-y-axis-labels")
            .attr("x", 0)
            .text((d) => d.short_display)
            .attr("text-anchor", "end")
            .attr("fill", "white")
            .attr("visibility", "hidden")
            .each(function () {
                maxWidth = Math.max(this.getBBox().width, maxWidth)
            })

        d3.select(this.node).selectAll(".test-y-axis-labels").remove()

        // accounts for the 6px line, and left padding of 20px
        const padding = 26

        return maxWidth + padding
    }

    clearGraph() {
        d3.select(this.node).selectAll(".axis").remove()
        d3.select(this.node).selectAll(".bar-label").remove()
        d3.select(this.node).selectAll(".bars").remove()
    }

    applyData() {
        this.data = this.props.data.slice(0, 10).toJS()

        const xAxisLabelWidth = this.getXAxisLabelWidth()
        const yAxisLabelWidth = this.getYAxisLabelWidth()

        // this is the space between the end of the x axis and the edge
        // of the svg.
        const rightBound = 50

        const xScale = d3.scale.linear().range([yAxisLabelWidth, this.svgWidth - rightBound])
        const yScale = d3.scale.ordinal().rangeRoundBands([xAxisLabelWidth / 2, this.svgHeight - xAxisLabelWidth], 0.2)

        const maxCount = d3.max(this.data, (d) => d.value)
        xScale.domain([0, maxCount])
        yScale.domain(d3.range(this.data.length))

        const barHeight = yScale.rangeBand()

        // tooltip instantiation!
        const tooltip = d3
            .tip()
            .attr("class", "d3-tip")
            .offset((d) => (xScale(d.value) < this.svgWidth / 2 ? [0, 5] : [-5, 0]))
            .direction((d) => (xScale(d.value) < this.svgWidth / 2 ? "e" : "n"))
            .html((d) => {
                const baseText = `<div>${d.long_display}: ${d.value.toLocaleString()}</div>`
                let instructions = ""

                if (this.props.selectedValues.includes(d.id)) {
                    instructions = `<hr><i style="color:gray;font-weight:normal;">Click "Apply Selected" to change results...</i>`
                }

                return baseText.concat(instructions)
            })

        d3.select(this.node).call(tooltip)

        const bars = d3.select(this.node).append("g").attr("class", "bars").selectAll("rect").data(this.data)

        // display bars
        bars.enter()
            .append("rect")
            .attr("class", "bar")
            .attr("height", barHeight)
            .attr("width", 0)
            .attr("y", (d, i) => yScale(i))
            .attr("x", xScale(0))
            .attr("fill", (d) => {
                const barBaseColor = this.props.colorConfigs.colorFunc
                    ? this.props.colorConfigs.colorFunc(d)
                    : this.props.colorConfigs.primaryColor

                return this.props.selectedValues.includes(d.id) ? this.props.colorConfigs.secondaryColor : barBaseColor
            })
            .on("click", (d) => {
                this.props.updateGraph(d.id)
                tooltip.hide(d)
                tooltip.show(d)
            })
            .on("mouseover", function (d) {
                tooltip.show(d, this)
                d3.select(this).style("opacity", "0.5")
            })
            .on("mouseout", function (d) {
                tooltip.hide(d)
                d3.select(this).style("opacity", "1")
            })
            .transition()
            .delay((d, i) => 50 * i)
            .attr("width", (d) => xScale(d.value) - yAxisLabelWidth)

        // used to align the number label on the bar
        // vertically
        const yTextAdjustment = 4

        // display number values of bars if long enough
        d3.select(this.node)
            .append("g")
            .attr("class", "bar-label")
            .selectAll(".bar-text")
            .data(this.data)
            .enter()
            .append("text")
            .attr("fill", "white")
            .attr("class", "bar-text")
            .attr("x", (d) => xScale(d.value) - 10)
            .attr("y", (_, i) => yScale(i) + barHeight / 2 + yTextAdjustment)
            .text((d) => {
                const barLength = xScale(d.value) - yAxisLabelWidth

                const valueString = formatNumberForDisplay(d.value)

                // doing a rough conversion between character number and
                // pixel width; adjust to taste
                const textLength = String(valueString).length * 8 + 10

                if (textLength < barLength) {
                    return valueString
                }
            })
            .attr("text-anchor", "end")

        // generate axes!
        const xAxis = d3.svg
            .axis()
            .scale(xScale)
            .orient("bottom")
            .tickFormat((d) => (d < 100 ? d3.format("d")(d) : formatNumberForDisplay(d)))

        // if the max count is less than 7, it'll be the tendency
        // of the graph to want to show ticks on the half. Let's not.
        // Quorum doesn't believe in halves. #nohalfmeasures
        if (maxCount < 7) {
            xAxis.ticks(maxCount)
        }

        const yAxis = d3.svg
            .axis()
            .scale(yScale)
            .orient("left")
            .tickFormat((_, i) => this.data[i].short_display)

        // call the axes!
        const xAxisGroup = d3
            .select(this.node)
            .append("g")
            .attr("class", "axis")
            .attr("transform", `translate(0,${this.svgHeight - xAxisLabelWidth})`)
            .transition()
            .duration(1000)
            .call(xAxis)

        if (maxCount > 100) {
            xAxisGroup
                .selectAll("text")
                .attr("transform", "rotate(-45)")
                .style("text-anchor", "end")
                .attr("dx", "-.75em")
                .attr("dy", ".5em")
        }

        d3.select(this.node)
            .append("g")
            .attr("class", "axis")
            .attr("transform", `translate(${yAxisLabelWidth},0)`)
            .transition()
            .duration(1000)
            .call(yAxis)
    }

    updateBars() {
        d3.select(this.node)
            .selectAll(".bar")
            .attr("fill", (d) => {
                const barBaseColor = this.props.colorConfigs.colorFunc
                    ? this.props.colorConfigs.colorFunc(d)
                    : this.props.colorConfigs.primaryColor

                return this.props.selectedValues.includes(d.id) ? this.props.colorConfigs.secondaryColor : barBaseColor
            })
    }

    render() {
        return <svg ref={(bar) => (this.node = bar)} id={this.props.svgId} />
    }
}
